import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { bytes, retention } from '../lib/format';
import { Card, Empty, ErrorNote, Field, Loading, PageHead, useResource, useToast } from '../components/ui';
import '../styles/capture.css';

/**
 * Record consent given offline: a paper form at a branch, a kiosk, an enquiry
 * desk at an event, a call to the customer interaction cell, an IVR flow. The
 * operator picks the collection point, names the person, says when the consent
 * was given, sets each purpose the way the person did, and attaches the form.
 * It goes through the same capture path as the widget, so it lands in the
 * ledger and on the person's record like any other consent.
 */

interface Point { id: string; name: string; channel: string; purposeIds: string[] }

interface Config {
  tenantName: string;
  channel: string;
  notice: { lang: string; version: number; dir?: string };
  purposes: Array<{
    purposeId: string; name: string; description: string; requiredForService: boolean;
    basis: 'consent' | 'legitimate_use'; basisSubtype?: string | null;
    retentionDays?: number | null; processors?: string[]; dataCategories?: string[];
  }>;
  languages: Array<{ code: string; endonym: string; dir: 'ltr' | 'rtl' }>;
  phoneChannel: boolean;
  smsAvailable: boolean;
  purposeKeys: Record<string, string>;
}

interface Attachment { id: string; filename: string; mime: string; sizeBytes: number; sha256: string }

interface Receipt {
  principalHash: string; channel: string; verification: string; capturedAt: string;
  events: Array<{ purposeId: string; action: string }>; requiredDeclined: string[];
  attachment: Attachment | null;
}

const CHANNEL_LABEL: Record<string, string> = {
  paper: 'Paper form', kiosk: 'Kiosk', offline: 'In person (branch, event)', callcentre: 'Call centre (agent)', ivr: 'Phone (IVR)',
};
const VERIFICATION_LABEL: Record<string, string> = {
  operator: 'Recorded by operator', otp_voice: 'Code read back on the call', none: 'Operator attested, no code',
};

/** datetime-local value for now, in the browser's zone. */
function nowLocal(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('Could not read the file.'));
    r.readAsDataURL(file);
  });
}

function detectType(v: string): 'email' | 'phone' {
  return v.includes('@') ? 'email' : 'phone';
}

export default function Capture() {
  const user = getUser();
  const canRecord = can(user, 'consents:capture');
  const [tab, setTab] = useState<'record' | 'register'>('record');
  const points = useResource<{ points: Point[] }>(() => api.get('/v1/consents/operator/points'));

  return (
    <>
      <PageHead title="Record consent" />
      <div className="tabs">
        <button className={tab === 'record' ? 'active' : ''} onClick={() => setTab('record')}>Record consent</button>
        <button className={tab === 'register' ? 'active' : ''} onClick={() => setTab('register')}>Upload register</button>
      </div>
      <ErrorNote error={points.error} />
      {points.loading && !points.data && <Loading what="Loading collection points" />}
      {points.data && points.data.points.length === 0 && (
        <Card>
          <Empty>
            No collection point takes consent offline. Add one under Collection points with channel paper form, kiosk, in person, call centre or IVR.
          </Empty>
        </Card>
      )}
      {points.data && points.data.points.length > 0 && (
        tab === 'record'
          ? <RecordForm points={points.data.points} canRecord={canRecord} />
          : <RegisterUpload points={points.data.points.filter((p) => p.channel !== 'callcentre' && p.channel !== 'ivr')} canRecord={canRecord} />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ record */

function RecordForm({ points, canRecord }: { points: Point[]; canRecord: boolean }) {
  const toast = useToast();
  const [pointId, setPointId] = useState(points[0]!.id);
  const [lang, setLang] = useState('en');
  const [identifier, setIdentifier] = useState('');
  const [type, setType] = useState<'email' | 'phone'>('phone');
  const [typeChosen, setTypeChosen] = useState(false);
  const [capturedAt, setCapturedAt] = useState(nowLocal());
  const [formRef, setFormRef] = useState('');
  const [note, setNote] = useState('');
  const [choices, setChoices] = useState<Record<string, boolean>>({});
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const [code, setCode] = useState('');
  const [sent, setSent] = useState<{ challengeId: string; to: string; expiresInMinutes: number } | null>(null);
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState<Receipt | null>(null);

  const point = points.find((p) => p.id === pointId) ?? points[0]!;
  const config = useResource<Config>(() => api.get(`/v1/consents/operator/config?collectionPointId=${pointId}&lang=${lang}`), [pointId, lang]);
  const cfg = config.data;
  const phone = cfg?.phoneChannel ?? false;

  // A new point or language starts the choices again: silence is not consent.
  useEffect(() => { setChoices({}); setSent(null); setCode(''); }, [pointId]);

  const asked = useMemo(() => (cfg?.purposes ?? []).filter((p) => p.basis !== 'legitimate_use'), [cfg]);
  const stated = useMemo(() => (cfg?.purposes ?? []).filter((p) => p.basis === 'legitimate_use'), [cfg]);
  const granted = asked.filter((p) => choices[p.purposeId]).length;

  async function upload(file: File | undefined) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast('Files are limited to 10 MB.', 'bad'); return; }
    setUploading(true);
    try {
      const mime = file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : file.name.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg');
      const res = await api.post<{ attachment: Attachment }>('/v1/consents/operator/attachments', { filename: file.name, mime, contentBase64: await toBase64(file) });
      setAttachment(res.attachment);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setUploading(false);
    }
  }

  async function sendCode() {
    if (type !== 'phone' || !identifier.trim()) { toast('Enter the caller\'s mobile number first.', 'bad'); return; }
    setSending(true);
    try {
      const res = await api.post<{ challengeId: string; to: string; expiresInMinutes: number }>('/v1/consents/operator/code', {
        collectionPointId: pointId, identifier: identifier.trim(), lang,
      });
      setSent(res);
      setCode('');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setSending(false);
    }
  }

  async function submit() {
    if (!identifier.trim()) { toast('Enter the person\'s email address or mobile number.', 'bad'); return; }
    if (phone && !sent && !note.trim()) { toast('Without a code, add a note saying how the caller was identified.', 'bad'); return; }
    if (phone && sent && code.trim().length < 4) { toast('Enter the code the caller read back.', 'bad'); return; }
    setBusy(true);
    try {
      const res = await api.post<Receipt>('/v1/consents/operator', {
        collectionPointId: pointId,
        principal: { identifier: identifier.trim(), identifierType: type },
        decisions: asked.map((p) => ({ purposeId: p.purposeId, granted: Boolean(choices[p.purposeId]) })),
        lang,
        capturedAt: new Date(capturedAt).toISOString(),
        formRef: formRef.trim() || undefined,
        attachmentId: attachment?.id,
        note: note.trim() || undefined,
        ...(phone && sent ? { challengeId: sent.challengeId, code: code.trim() } : {}),
      });
      setReceipt(res);
      toast(`Recorded ${res.events.length} decision${res.events.length === 1 ? '' : 's'}.`, 'ok');
      setIdentifier(''); setTypeChosen(false); setFormRef(''); setNote(''); setChoices({}); setAttachment(null);
      setSent(null); setCode(''); setCapturedAt(nowLocal());
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cap-grid">
      <Card title="Collection point and person">
        <Field label="Collection point">
          <select value={pointId} onChange={(e) => setPointId(e.target.value)}>
            {points.map((p) => <option key={p.id} value={p.id}>{p.name} · {CHANNEL_LABEL[p.channel] ?? p.channel}</option>)}
          </select>
        </Field>
        <div className="cap-two">
          <Field label="Identifier type">
            <select value={type} onChange={(e) => { setType(e.target.value as 'email' | 'phone'); setTypeChosen(true); }}>
              <option value="phone">Mobile number</option>
              <option value="email">Email address</option>
            </select>
          </Field>
          <Field label={type === 'phone' ? 'Mobile number' : 'Email address'} help="Hashed on entry; the identifier is stored encrypted.">
            <input
              type="text" autoComplete="off" value={identifier}
              placeholder={type === 'phone' ? '+91 98xxx xxxxx' : 'name@example.com'}
              onChange={(e) => { setIdentifier(e.target.value); setSent(null); if (!typeChosen) setType(detectType(e.target.value)); }}
            />
          </Field>
        </div>
        <div className="cap-two">
          <Field label="Date and time consent was given" help="Defaults to now. Set it earlier for a form signed before today.">
            <input type="datetime-local" value={capturedAt} max={nowLocal()} onChange={(e) => setCapturedAt(e.target.value)} />
          </Field>
          <Field label="Language the notice was shown in">
            <select value={lang} onChange={(e) => setLang(e.target.value)}>
              {(cfg?.languages ?? [{ code: 'en', endonym: 'English', dir: 'ltr' as const }]).map((l) => <option key={l.code} value={l.code}>{l.endonym}</option>)}
            </select>
          </Field>
        </div>

        {phone && cfg && (
          cfg.smsAvailable ? (
            <Field label="Verify the caller" help="A six-digit code goes to the mobile number. Ask the caller to read it back.">
              <div className="cap-code">
                <input type="text" inputMode="numeric" maxLength={6} value={code} disabled={!sent} placeholder={sent ? 'Code read back' : 'Send the code first'} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
                <button type="button" onClick={sendCode} disabled={sending || !canRecord || type !== 'phone' || !identifier.trim()}>{sending ? 'Sending…' : sent ? 'Send again' : 'Send code'}</button>
              </div>
              {sent && <div className="cap-sent">Code sent to {sent.to}. It expires in {sent.expiresInMinutes} minutes.</div>}
            </Field>
          ) : (
            <div className="note warn" style={{ marginBottom: 12 }}>
              No SMS channel is configured, so no code can be sent. Record with a note saying how the caller was identified; the record will show the operator attested it. Add an SMS channel under Settings, Channels.
            </div>
          )
        )}

        <Field label="Form or call reference" help="The number on the paper form or the call log.">
          <input type="text" value={formRef} maxLength={80} onChange={(e) => setFormRef(e.target.value)} placeholder="BR-2026-0412" />
        </Field>
        <Field label="Scanned form or photo" help="PDF, JPG or PNG up to 10 MB. The digest is taken at upload and written on the record.">
          <div className="cap-file">
            <label className="btn small" style={{ cursor: uploading ? 'progress' : 'pointer' }}>
              {uploading ? 'Uploading…' : attachment ? 'Replace file' : 'Add file'}
              <input type="file" hidden disabled={uploading || !canRecord} accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
            {attachment && (
              <span className="cap-file-meta">
                {attachment.filename} · {bytes(attachment.sizeBytes)} · <span className="mono" title={attachment.sha256}>{attachment.sha256.slice(0, 12)}…</span>
                {' '}<button type="button" className="small" onClick={() => setAttachment(null)}>Remove</button>
              </span>
            )}
          </div>
        </Field>
        <Field label={phone ? 'Operator note' : 'Operator note (optional)'} help={phone ? 'Required when no code is read back: say how the caller was identified.' : undefined}>
          <textarea value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} rows={3} />
        </Field>

        <div className="row" style={{ marginTop: 6 }}>
          <button className="primary" onClick={submit} disabled={busy || !canRecord || !cfg || !identifier.trim()}>{busy ? 'Recording…' : 'Record consent'}</button>
          <span className="small muted">
            {asked.length ? `${granted} of ${asked.length} purpose${asked.length === 1 ? '' : 's'} on` : ''}
            {!canRecord ? ' · Your role can view this screen but not record.' : ''}
          </span>
        </div>

        {receipt && (
          <div className="note ok cap-receipt">
            <strong>Recorded for <Link to={`/principals/${receipt.principalHash}`}>record {receipt.principalHash.slice(0, 12)}…</Link></strong>
            {receipt.events.filter((e) => e.action === 'granted').length} given, {receipt.events.filter((e) => e.action === 'declined').length} declined
            {' · '}{CHANNEL_LABEL[receipt.channel] ?? receipt.channel} · {VERIFICATION_LABEL[receipt.verification] ?? receipt.verification}
            {receipt.attachment ? ` · ${receipt.attachment.filename} attached` : ''}
            {receipt.requiredDeclined.length ? <div style={{ marginTop: 4 }}>A purpose needed to provide the service was declined. The service may not be provided.</div> : null}
          </div>
        )}
      </Card>

      <Card title={cfg ? `Choices · ${point.name}` : 'Choices'}>
        <ErrorNote error={config.error} />
        {config.loading && !cfg && <Loading what="Loading the notice" />}
        {cfg && (
          <>
            <div className="small muted" style={{ marginBottom: 8 }}>
              Notice v{cfg.notice.version} · {cfg.languages.find((l) => l.code === cfg.notice.lang)?.endonym ?? cfg.notice.lang}. Set each switch the way the person marked the form or answered on the call.
            </div>
            <div className="cap-ledger" dir={cfg.notice.dir ?? 'ltr'}>
              {asked.map((p) => (
                <PurposeRow key={p.purposeId} p={p} on={Boolean(choices[p.purposeId])} disabled={!canRecord} onChange={(v) => setChoices((c) => ({ ...c, [p.purposeId]: v }))} />
              ))}
              {stated.map((p) => <PurposeRow key={p.purposeId} p={p} stated />)}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function PurposeRow({ p, on, stated, disabled, onChange }: {
  p: Config['purposes'][number]; on?: boolean; stated?: boolean; disabled?: boolean; onChange?: (v: boolean) => void;
}) {
  const id = `cap-${p.purposeId}`;
  const processors = p.processors ?? [];
  return (
    <div className={`cap-row${stated ? ' cap-row-stated' : ''}`}>
      <div className="cap-row-main">
        <div className="cap-row-head">
          <label className="cap-row-name" htmlFor={stated ? undefined : id}>{p.name}</label>
          {stated
            ? <span className="cap-chip cap-chip-on">Always on</span>
            : <span className={`cap-chip ${p.requiredForService ? 'cap-chip-req' : 'cap-chip-opt'}`} title={p.requiredForService ? 'Needed to provide the service' : 'Optional'}>{p.requiredForService ? 'Required' : 'Optional'}</span>}
        </div>
        {p.description && <p className="cap-row-desc">{p.description}</p>}
        <div className="cap-facts">
          <span>Kept {retention(p.retentionDays ?? null).toLowerCase()}</span>
          <span>{processors.length === 0 ? 'Not shared' : processors.length === 1 ? `1 recipient: ${processors[0]}` : `${processors.length} recipients`}</span>
          {stated && <span>Legal ground: {p.basisSubtype || 'S.7'}</span>}
        </div>
      </div>
      <div>
        {stated ? (
          <span className="cap-state">Stated, no decision</span>
        ) : (
          <span className="cap-switch">
            <input id={id} type="checkbox" checked={Boolean(on)} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} />
            <span className="cap-track" /><span className="cap-knob" />
          </span>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ register */

interface RegisterRow { identifier: string; identifier_type: string; captured_at: string; purposes_allowed: string; form_ref: string }
interface RegisterResult { dryRun: boolean; received: number; accepted: number; rejected: number; recorded: number; errors: Array<{ row: number; problem: string }> }

const COLUMNS = ['identifier', 'identifier_type', 'captured_at', 'purposes_allowed', 'form_ref'] as const;

/** A small CSV reader: quoted fields, commas inside quotes, CRLF. Header row required. */
function parseCsv(text: string): { columns: string[]; rows: RegisterRow[]; problem?: string } {
  const lines: string[][] = [];
  let field = '';
  let row: string[] = [];
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 1; } else quoted = false;
      } else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field); field = '';
      if (row.some((c) => c.trim())) lines.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.some((c) => c.trim())) lines.push(row);
  if (!lines.length) return { columns: [], rows: [], problem: 'The file is empty.' };

  const header = lines[0]!.map((c) => c.trim().toLowerCase().replace(/[\s-]+/g, '_'));
  const index = (name: string) => header.indexOf(name);
  if (index('identifier') < 0) return { columns: header, rows: [], problem: 'The first row must name the columns, and one of them must be "identifier".' };
  const rows = lines.slice(1).map((cells) => {
    const get = (name: string) => { const i = index(name); return i >= 0 ? (cells[i] ?? '').trim() : ''; };
    return { identifier: get('identifier'), identifier_type: get('identifier_type'), captured_at: get('captured_at'), purposes_allowed: get('purposes_allowed'), form_ref: get('form_ref') };
  });
  return { columns: header, rows };
}

function RegisterUpload({ points, canRecord }: { points: Point[]; canRecord: boolean }) {
  const toast = useToast();
  const [pointId, setPointId] = useState(points[0]?.id ?? '');
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ReturnType<typeof parseCsv> | null>(null);
  const [result, setResult] = useState<RegisterResult | null>(null);
  const [busy, setBusy] = useState<'check' | 'record' | null>(null);
  const keys = useResource<{ purposes: Array<{ key: string; name: string; required: boolean }> }>(
    () => (pointId ? api.get(`/v1/consents/operator/register-keys?collectionPointId=${pointId}`) : Promise.resolve({ purposes: [] })), [pointId],
  );

  if (!points.length) {
    return <Card><Empty>A register is uploaded against a paper, kiosk or in-person point. None is set up.</Empty></Card>;
  }

  async function onFile(file: File | undefined) {
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setParsed(parseCsv(await file.text()));
  }

  async function run(dryRun: boolean) {
    if (!parsed?.rows.length) return;
    setBusy(dryRun ? 'check' : 'record');
    try {
      const res = await api.post<RegisterResult>('/v1/consents/operator/register', { collectionPointId: pointId, rows: parsed.rows, dryRun, fileName });
      setResult(res);
      if (!dryRun) toast(`${res.recorded} row${res.recorded === 1 ? '' : 's'} recorded${res.rejected ? `, ${res.rejected} with problems` : ''}.`, res.recorded ? 'ok' : 'bad');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }

  function template() {
    const example = ['+919812004411', 'phone', '02/10/2026 11:30', (keys.data?.purposes ?? []).map((p) => p.key).join('|'), 'BR-2026-0412'];
    const csv = `${COLUMNS.join(',')}\n${example.join(',')}\n`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'consent-register-template.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const checked = result?.dryRun === true && parsed && result.received === parsed.rows.length;

  return (
    <div className="cap-grid">
      <Card title="Register file">
        <Field label="Collection point">
          <select value={pointId} onChange={(e) => { setPointId(e.target.value); setResult(null); }}>
            {points.map((p) => <option key={p.id} value={p.id}>{p.name} · {CHANNEL_LABEL[p.channel] ?? p.channel}</option>)}
          </select>
        </Field>
        <Field label="CSV file" help="One row per form. The first row names the columns.">
          <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} />
        </Field>
        {parsed?.problem && <div className="note bad">{parsed.problem}</div>}
        {parsed && !parsed.problem && (
          <div className="small muted" style={{ marginBottom: 10 }}>
            {fileName}: {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'} read. Columns: {parsed.columns.join(', ')}.
          </div>
        )}
        <div className="row">
          <button onClick={() => run(true)} disabled={!!busy || !parsed?.rows.length}>{busy === 'check' ? 'Checking…' : 'Check rows'}</button>
          <button className="primary" onClick={() => run(false)} disabled={!!busy || !canRecord || !checked || !result || result.accepted === 0}>
            {busy === 'record' ? 'Recording…' : result && checked ? `Record ${result.accepted} row${result.accepted === 1 ? '' : 's'}` : 'Record rows'}
          </button>
          <button className="small" onClick={template}>Template</button>
        </div>
        {!checked && parsed && !parsed.problem && <div className="small muted" style={{ marginTop: 6 }}>Check the rows first. Nothing is recorded until you do.</div>}

        {result && (
          <>
            <div className="cap-counts">
              <div><strong>{result.received}</strong><span>rows</span></div>
              <div><strong>{result.dryRun ? result.accepted : result.recorded}</strong><span>{result.dryRun ? 'would record' : 'recorded'}</span></div>
              <div><strong>{result.rejected}</strong><span>with problems</span></div>
            </div>
            {result.errors.length > 0 && (
              <div className="table-scroll" style={{ maxHeight: 320, overflowY: 'auto' }}>
                <table className="data">
                  <thead><tr><th>Row</th><th>Problem</th></tr></thead>
                  <tbody>{result.errors.map((e) => <tr key={e.row}><td className="num">{e.row}</td><td className="small">{e.problem}</td></tr>)}</tbody>
                </table>
              </div>
            )}
            {result.errors.length === 0 && <div className="note ok">{result.dryRun ? 'Every row passes.' : 'Every row recorded.'}</div>}
          </>
        )}
      </Card>

      <Card title="Columns">
        <div className="cap-reg-help">
          <table className="data">
            <tbody>
              <tr><td><code>identifier</code></td><td>Email address or mobile number.</td></tr>
              <tr><td><code>identifier_type</code></td><td><code>email</code> or <code>phone</code>. Blank: worked out from the identifier.</td></tr>
              <tr><td><code>captured_at</code></td><td>When the form was signed: <code>02/10/2026 11:30</code> (IST) or ISO 8601. Blank: now. Never in the future.</td></tr>
              <tr><td><code>purposes_allowed</code></td><td>Purpose keys the person ticked, separated by <code>|</code>. Every other purpose on the point is recorded as declined.</td></tr>
              <tr><td><code>form_ref</code></td><td>The number on the form. Optional.</td></tr>
            </tbody>
          </table>
          <div style={{ marginTop: 14, marginBottom: 6 }}>Purpose keys on this point</div>
          {keys.loading && !keys.data && <Loading />}
          {keys.data && (
            <table className="data">
              <thead><tr><th>Key</th><th>Purpose</th><th /></tr></thead>
              <tbody>
                {keys.data.purposes.map((p) => <tr key={p.key}><td><code>{p.key}</code></td><td>{p.name}</td><td className="small muted">{p.required ? 'Required' : 'Optional'}</td></tr>)}
              </tbody>
            </table>
          )}
          <div style={{ marginTop: 12 }}>Rows are checked before anything is recorded. A row with a problem is reported by its number and skipped; fix it and upload the file again.</div>
        </div>
      </Card>
    </div>
  );
}
